import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { Camera, X, Loader2, ImagePlus } from 'lucide-react';
import { API, useAuth } from '../../App';
import { useHubT } from './hubStrings';

const MAX_PHOTOS = 3;
// Compression target agreed with product: longest edge ≤ 1600px, JPEG q≈0.8.
const COMPRESS_OPTS = {
  maxSizeMB: 5,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.8,
};

const heicToJpeg = async (file) => {
  // Lazy-loaded so heic2any's ~1.5 MB payload is only pulled when the user
  // actually picks an HEIC on iPhone.
  const heic2any = (await import('heic2any')).default;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const output = Array.isArray(blob) ? blob[0] : blob;
  return new File([output], (file.name || 'photo').replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
};

const isHeic = (file) => {
  const n = (file.name || '').toLowerCase();
  const t = (file.type || '').toLowerCase();
  return n.endsWith('.heic') || n.endsWith('.heif') || t === 'image/heic' || t === 'image/heif';
};

/**
 * Compact 3-slot uploader for the "Share Local Information" form.
 *
 *  - HEIC → JPEG on the client (iPhone default format).
 *  - Resize/compress to 1600 px longest edge, JPEG q≈0.8, before upload.
 *  - Immediate upload to Supabase via /api/community-reports/upload-photo.
 *  - Returns the resulting public URL list to the parent via onChange.
 */
export const CommunityReportPhotoUploader = ({ photos, onChange, disabled }) => {
  const { user } = useAuth();
  const hubT = useHubT();
  const [busySlots, setBusySlots] = useState({});
  const authHeaders = user ? { Authorization: `Bearer ${user.token}` } : {};

  const addPhotoUrl = useCallback((url) => {
    onChange([...(photos || []), url].slice(0, MAX_PHOTOS));
  }, [photos, onChange]);

  // Fire-and-forget cleanup of an uploaded-but-not-yet-submitted photo. The
  // server verifies the caller owns it and that no report references it, so
  // this is safe even if the user simultaneously submits (409 → no delete).
  const requestServerDelete = useCallback((url) => {
    if (!url || !user?.token) return;
    axios.delete(`${API}/community-reports/photo`, {
      params: { url },
      headers: authHeaders,
    }).catch(() => { /* best-effort cleanup */ });
  }, [user, authHeaders]);

  const removePhoto = (idx) => {
    if (disabled) return;
    const next = [...(photos || [])];
    const [removed] = next.splice(idx, 1);
    onChange(next);
    requestServerDelete(removed);
  };

  const uploadOne = async (file, slotKey) => {
    setBusySlots(s => ({ ...s, [slotKey]: true }));
    try {
      let working = file;
      if (isHeic(working)) {
        try {
          working = await heicToJpeg(working);
        } catch {
          toast.error(hubT('photoHeicFailed'));
          return;
        }
      }
      if (!working.type?.startsWith('image/')) {
        toast.error(hubT('photoInvalidType'));
        return;
      }
      let compressed;
      try {
        compressed = await imageCompression(working, COMPRESS_OPTS);
      } catch {
        compressed = working; // fall back to original — server still enforces 5MB cap.
      }
      const form = new FormData();
      // Force a .jpg filename so the server routes it through the JPEG branch.
      const outName = (compressed.name || 'photo.jpg').replace(/\.(png|webp|heic|heif)$/i, '.jpg');
      form.append('file', compressed, outName.endsWith('.jpg') || outName.endsWith('.jpeg') ? outName : `${outName}.jpg`);
      const res = await axios.post(`${API}/community-reports/upload-photo`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        addPhotoUrl(res.data.url);
      } else {
        toast.error(hubT('photoUploadFailed'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || hubT('photoUploadFailed'));
    } finally {
      setBusySlots(s => {
        const next = { ...s };
        delete next[slotKey];
        return next;
      });
    }
  };

  const handleFiles = (files, startingSlot) => {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - (photos?.length || 0);
    const list = Array.from(files).slice(0, room);
    list.forEach((f, i) => uploadOne(f, `slot-${startingSlot}-${i}-${Date.now()}`));
  };

  const filledCount = photos?.length || 0;
  const showAddTile = filledCount < MAX_PHOTOS;
  // Render existing photos + a single "add" tile. Users tap the tile to open
  // the OS picker (which offers Camera + Photo Library on mobile).
  return (
    <div data-testid="community-report-photo-uploader">
      <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
        <Camera className="h-3.5 w-3.5" />
        {hubT('photosLabel')} <span className="font-normal text-gray-400">({filledCount}/{MAX_PHOTOS})</span>
      </label>
      <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">{hubT('photosHelp')}</p>
      <div className="grid grid-cols-3 gap-2">
        {(photos || []).map((url, idx) => {
          const slotKey = `existing-${idx}`;
          const isBusy = !!busySlots[slotKey];
          return (
            <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50" data-testid={`photo-slot-${idx}`}>
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(idx)}
                disabled={disabled || isBusy}
                aria-label={hubT('removePhoto')}
                className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full h-6 w-6 flex items-center justify-center transition-colors"
                data-testid={`photo-remove-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <label className="absolute bottom-1 right-1 bg-white/85 hover:bg-white text-slate-700 rounded-full text-[10px] font-semibold px-2 py-0.5 cursor-pointer">
                {hubT('replace')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    // Remove first so uploadOne re-inserts at the tail — keeps
                    // ordering predictable when replacing a middle photo.
                    removePhoto(idx);
                    await uploadOne(file, `replace-${idx}-${Date.now()}`);
                  }}
                  data-testid={`photo-replace-input-${idx}`}
                />
              </label>
              {isBusy && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
                </div>
              )}
            </div>
          );
        })}
        {showAddTile && (
          <label
            className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors ${
              disabled
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-300 text-slate-600 hover:border-slate-500 hover:text-slate-800 cursor-pointer'
            }`}
            data-testid="photo-add-tile"
          >
            {Object.keys(busySlots).length > 0 ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <ImagePlus className="h-6 w-6" />
                <span>{hubT('addPhoto')}</span>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              disabled={disabled}
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files, filledCount);
                e.target.value = '';
              }}
              data-testid="photo-add-input"
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default CommunityReportPhotoUploader;
